import React from 'react';

import { SnackbarActionTypes, useSnackbarDispatch, useSnackbarState } from 'contexts/snackbar/SnackbarContext';

import { Alert, AlertTitle, Box, Button, styled, useTheme } from '@mui/material';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import { CircleCheckBig } from 'lucide-react';
import { useIntl } from 'react-intl';

type SuccessSnackbarProps = {};

const SnackbarButton = styled(Button)(({ theme }) => ({
  color: theme.palette.darkBlue.main,
  borderColor: theme.palette.darkBlue.main,
  '&:hover': {
    color: theme.palette.darkBlue.main,
    borderColor: theme.palette.darkBlue.main,
  },
}));

const Root = styled(Alert)(({ theme }) => ({
  paddingLeft: '49px',
  backgroundColor: theme.palette.green.o50,
  ['& .MuiAlert-message']: {
    width: '100%',
  },
  ['& .MuiAlert-action']: {
    button: {
      color: theme.palette.darkBlue.main,
    },
    marginLeft: 0,
    color: theme.palette.darkBlue.main,
  },
}));

const SuccessSnackbar: React.FC<SuccessSnackbarProps> = () => {
  const { msg, isSuccess, action } = useSnackbarState();
  const theme = useTheme();
  const dispatch = useSnackbarDispatch();
  const intl = useIntl();

  const handleClose = () => {
    dispatch({ type: SnackbarActionTypes.CLEAR_SNACKBAR });
  };

  const handleAction = () => {
    action?.onClick();
    handleClose();
  };

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      onClose={handleClose}
      autoHideDuration={action ? null : 3000}
      open={isSuccess}
      slots={{
        transition: Slide,
      }}
      key={Slide.name}>
      <Root elevation={6} variant="filled" onClose={handleClose} icon={false}>
        <Box
          sx={{
            padding: theme.spacing(4),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <CircleCheckBig color={theme.palette.darkBlue.main} size={32} />
          <AlertTitle
            sx={{
              padding: theme.spacing(3, 0),
              fontSize: 20,
              color: theme.palette.darkBlue.main,
            }}>
            {msg || intl.formatMessage({ id: 'snackbar.success.default' })}
          </AlertTitle>
          <SnackbarButton
            variant="outlined"
            fullWidth
            sx={{
              color: theme.palette.darkBlue.main,
              borderColor: theme.palette.darkBlue.main,
            }}
            onClick={action ? handleAction : handleClose}>
            {action?.label || intl.formatMessage({ id: 'general.ok' })}
          </SnackbarButton>
        </Box>
      </Root>
    </Snackbar>
  );
};

export default SuccessSnackbar;
